import { useState, FormEvent } from "react";

const companyTypes = ["VoIP Operator", "Reseller", "Call Center", "CPaaS Platform", "Enterprise", "Other"];
const services = ["Voice Termination", "DID / Origination", "SIP Trunking", "Custom Routing"];
const messengers = ["Email", "Telegram", "WhatsApp", "Teams"];

export default function InquiryForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState<string>("");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  const toggleService = (s: string) => {
    setSelectedServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // Honeypot check
    const hp = ((fd.get("website_alt") as string) || "").trim();
    if (hp) {
      setStatus("success");
      form.reset();
      setSelectedServices([]);
      return;
    }
    fd.delete("website_alt");

    // Append multi-select services
    fd.delete("services");
    selectedServices.forEach((s) => fd.append("services", s));

    // Textarea length check
    const message = ((fd.get("message") as string) || "");
    if (message.length > 500) {
      setFormError("Please keep your message under 500 characters.");
      setStatus("error");
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      fd.forEach((value, key) => {
        body.append(key, value as string);
      });

      const response = await fetch("https://readdy.ai/api/form/d9tq5ulca23r0tr1am1g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || "";

      if (response.ok && parsed?.code === "OK" && !serverMsg.toLowerCase().includes("spam")) {
        setStatus("success");
        form.reset();
        setSelectedServices([]);
      } else {
        setFormError(serverMsg || "Something went wrong. Please try again or email sales@voxmaster.com.");
        setStatus("error");
      }
    } catch {
      setFormError("Network error. Please try again or email sales@voxmaster.com.");
      setStatus("error");
    }
  };

  return (
    <section id="inquiry" className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">Get in touch</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
              Talk to a real carrier engineer.
            </h2>
            <p className="mt-5 text-foreground-700 leading-relaxed">
              Tell us about your traffic and we&apos;ll come back with a targeted LCR quote, test IPs, and a suggested integration plan. Usually within one business day.
            </p>
            <div className="mt-8 flex flex-col gap-4">
              {[
                { icon: "ri-mail-line", label: "sales@voxmaster.com" },
                { icon: "ri-telegram-2-line", label: "@voxmaster_sales" },
                { icon: "ri-whatsapp-line", label: "+1 702-983-0030" },
                { icon: "ri-time-line", label: "24/7 NOC · Response < 15 min" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i className={`${c.icon} text-lg w-5 h-5 flex items-center justify-center`} />
                  </span>
                  <span className="text-sm text-foreground-800">{c.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-xl border border-background-200 bg-background-100 p-5 flex items-start gap-3">
              <i className="ri-shield-check-line text-accent-600 w-5 h-5 flex items-center justify-center mt-0.5" />
              <div className="text-xs text-foreground-700 leading-relaxed">
                We never share your traffic details with third parties. All commercial and technical discussions can proceed under NDA on request.
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="voxmaster-home-inquiry"
              className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Full name *</label>
                  <input
                    required
                    type="text"
                    name="name"
                    className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Work email *</label>
                  <input
                    required
                    type="email"
                    name="email"
                    className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950"
                    placeholder="jane@company.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Company *</label>
                  <input
                    required
                    type="text"
                    name="company"
                    className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950"
                    placeholder="Acme Telecom"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Company type *</label>
                  <select
                    required
                    name="company_type"
                    defaultValue=""
                    className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950 cursor-pointer"
                  >
                    <option value="" disabled>Select one</option>
                    {companyTypes.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Top destinations</label>
                  <input
                    type="text"
                    name="destinations"
                    className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950"
                    placeholder="e.g. USA Mobile, UK Mobile, Nigeria"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Monthly minute volume</label>
                  <select
                    name="volume"
                    defaultValue=""
                    className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950 cursor-pointer"
                  >
                    <option value="" disabled>Select volume</option>
                    <option>Under 100k</option>
                    <option>100k – 1M</option>
                    <option>1M – 10M</option>
                    <option>10M – 100M</option>
                    <option>100M+</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-medium text-foreground-700 mb-2">Services of interest</label>
                <div className="flex flex-wrap gap-2">
                  {services.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleService(s)}
                      className={`text-xs px-3 py-1.5 rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
                        selectedServices.includes(s)
                          ? "bg-primary-500 border-primary-500 text-background-50"
                          : "bg-background-50 border-background-300 text-foreground-700 hover:border-primary-400"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Preferred messenger</label>
                <div className="flex flex-wrap gap-2">
                  {messengers.map((m) => (
                    <label
                      key={m}
                      className="text-xs px-3 py-1.5 rounded-full border border-background-300 text-foreground-700 cursor-pointer whitespace-nowrap hover:border-primary-400 has-[:checked]:bg-accent-100 has-[:checked]:border-accent-500 has-[:checked]:text-accent-900"
                    >
                      <input type="radio" name="messenger" value={m} className="hidden" />
                      {m}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Tell us more (optional, 500 chars)</label>
                <textarea
                  name="message"
                  maxLength={500}
                  rows={4}
                  className="w-full text-sm rounded-md border border-background-300 focus:border-primary-500 focus:outline-none px-3 py-2.5 bg-background-50 text-foreground-950 resize-none"
                  placeholder="Current pain points, integration timeline, LCR notes…"
                />
              </div>

              {/* Anti-spam honeypot — must not be filled by real users */}
              <div className="vx-hp-shield" aria-hidden="true">
                <label>
                  Alternate website
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </label>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                <p className="text-xs text-foreground-500">
                  By submitting, you agree to be contacted by Voxmaster about your inquiry.
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-70 text-background-50 px-6 py-3 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
                >
                  {status === "submitting" ? "Sending…" : "Request a quote"}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </button>
              </div>

              {status === "success" && (
                <div className="mt-5 rounded-md border border-accent-300 bg-accent-100 text-accent-900 text-sm px-4 py-3 flex items-start gap-2">
                  <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center mt-0.5" />
                  Thanks — we&apos;ve received your inquiry. A carrier engineer will reach out within one business day.
                </div>
              )}
              {status === "error" && (
                <div className="mt-5 rounded-md border border-primary-300 bg-primary-100 text-primary-900 text-sm px-4 py-3 flex items-start gap-2">
                  <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center mt-0.5" />
                  {formError || "Something went wrong. Please try again."}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}